import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  CheckCircle2,
  FileText,
  Image,
  Mail,
  MessageSquareText,
  Paperclip,
  Phone,
  Send,
  Trash2,
  UserRound,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { PriorityBadge, StatusBadge } from "../components/StatusBadge";
import { Button, ButtonLink, Card, PageHeader, Select, Skeleton, Textarea } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENTS_TOTAL_SIZE,
  MAX_ATTACHMENT_SIZE,
  formatFileSize,
  isAllowedAttachment,
  isImageAttachment,
} from "../lib/attachments";
import { formatDate } from "../lib/format";

function plainText(value?: string) {
  if (!value) return "";
  const documentValue = new DOMParser().parseFromString(value, "text/html");
  return documentValue.body.textContent?.trim() ?? value;
}

function attachmentName(url: string, index: number) {
  const fallback = `Arquivo ${index + 1}`;
  try {
    return decodeURIComponent(url.split("/").pop() || fallback);
  } catch {
    return fallback;
  }
}

function OpeningAttachment({
  ticketId,
  attachment,
  index,
  onDownload,
}: {
  ticketId: number;
  attachment: string;
  index: number;
  onDownload: () => void;
}) {
  const [source, setSource] = useState("");
  const [failed, setFailed] = useState(false);
  const isImage = isImageAttachment(attachment);

  useEffect(() => {
    if (!isImage) return;

    let active = true;
    let objectUrl = "";

    api.downloadTicketAttachment(ticketId, attachment)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setSource(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment, isImage, ticketId]);

  return (
    <button
      type="button"
      className="opening-image"
      onClick={onDownload}
      title="Baixar anexo"
    >
      {isImage && source && !failed ? (
        <img
          src={source}
          alt={attachmentName(attachment, index)}
          onError={() => setFailed(true)}
        />
      ) : (
        <span>{isImage ? <Image /> : <FileText />}</span>
      )}
      <small>{attachmentName(attachment, index)}</small>
    </button>
  );
}

export function TicketDetailPage({ ticketId }: { ticketId: string }) {
  const id = Number(ticketId);
  const { session } = useAuth();
  const isMira = session?.codigoEmpresa === 52;
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState("");
  const [statusId, setStatusId] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const ticket = useQuery({
    queryKey: ["ticket", session?.codigoEmpresa, id],
    queryFn: () => api.getTicket(session!.codigoEmpresa, id),
    enabled: Boolean(session?.codigoEmpresa && Number.isFinite(id)),
  });
  const statuses = useQuery({
    queryKey: ["ticket-statuses"],
    queryFn: api.getStatuses,
  });
  const availableStatuses = useMemo(() => {
    if (session?.codigoEmpresa === 52) return statuses.data ?? [];
    const customerStatusIds = new Set([10, 4, 6]);
    return (statuses.data ?? []).filter((item) => customerStatusIds.has(item.id));
  }, [session?.codigoEmpresa, statuses.data]);

  const steps = useMemo(
    () =>
      [...(ticket.data?.tramites ?? [])].sort(
        (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime(),
      ),
    [ticket.data?.tramites],
  );

  const addStep = useMutation({
    mutationFn: async () => {
      const stepId = await api.addTicketStep(
        id,
        Number(statusId),
        message.trim() || "Arquivo anexado ao chamado.",
      );
      let attachmentsUploaded = true;
      if (files.length) {
        try {
          await api.uploadTicketAttachments(id, stepId, files);
        } catch {
          attachmentsUploaded = false;
        }
      }
      return { attachmentsUploaded, attachmentCount: files.length };
    },
    onSuccess: ({ attachmentsUploaded, attachmentCount }) => {
      if (attachmentsUploaded) {
        toast.success(
          attachmentCount
            ? `Atualização registrada com ${attachmentCount} anexo(s).`
            : "Atualização registrada no chamado.",
        );
      } else {
        toast.warning("A atualização foi registrada, mas os arquivos não puderam ser anexados.");
      }
      setMessage("");
      setFiles([]);
      setStatusId("");
      queryClient.invalidateQueries({ queryKey: ["ticket", session?.codigoEmpresa, id] });
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
    },
    onError: () => toast.error("Não foi possível registrar a atualização."),
  });

  const updateType = useMutation({
    mutationFn: (tipo: number) => api.updateTicketType(id, tipo),
    onSuccess: () => {
      toast.success("Tipo do chamado atualizado.");
      queryClient.invalidateQueries({ queryKey: ["ticket", session?.codigoEmpresa, id] });
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
    },
    onError: () => toast.error("Não foi possível atualizar o tipo do chamado."),
  });

  function submitStep(event: FormEvent) {
    event.preventDefault();
    if (!statusId || (message.trim().length < 3 && files.length === 0)) {
      toast.error("Selecione o status e escreva uma atualização ou anexe um arquivo.");
      return;
    }
    addStep.mutate();
  }

  function addFiles(nextFiles: File[]) {
    const merged = [...files];

    for (const file of nextFiles) {
      if (!isAllowedAttachment(file.name)) {
        toast.error(`O formato de "${file.name}" não é permitido.`);
        continue;
      }
      if (file.size === 0 || file.size > MAX_ATTACHMENT_SIZE) {
        toast.error(`"${file.name}" deve ter no máximo 10 MB.`);
        continue;
      }
      const duplicated = merged.some(
        (current) =>
          current.name === file.name &&
          current.size === file.size &&
          current.lastModified === file.lastModified,
      );
      if (!duplicated) merged.push(file);
    }

    if (merged.length > MAX_ATTACHMENTS) {
      toast.error(
        `Você pode enviar no máximo ${MAX_ATTACHMENTS} arquivos por atualização.`,
      );
      return;
    }
    if (
      merged.reduce((total, file) => total + file.size, 0) >
      MAX_ATTACHMENTS_TOTAL_SIZE
    ) {
      toast.error("O total dos arquivos não pode ultrapassar 25 MB.");
      return;
    }
    setFiles(merged);
  }

  async function downloadAttachment(attachment: string, attachmentIndex: number) {
    try {
      const blob = await api.downloadTicketAttachment(id, attachment);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = attachmentName(attachment, attachmentIndex);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível baixar o anexo.");
    }
  }

  if (ticket.isLoading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-28 w-full rounded-2xl" />
        <Skeleton className="h-80 w-full rounded-2xl" />
      </div>
    );
  }

  if (ticket.isError || !ticket.data?.ticket) {
    return (
      <Card className="error-state">
        <span>404</span>
        <h1>Chamado não encontrado</h1>
        <p>Ele pode não existir ou não estar disponível para sua empresa.</p>
        <ButtonLink to="/chamados" variant="secondary">Voltar aos chamados</ButtonLink>
      </Card>
    );
  }

  const data = ticket.data;

  return (
    <div className="page-stack max-w-[1180px]">
      <PageHeader
        eyebrow="Detalhes do atendimento"
        title={`Chamado #${data.ticket}`}
        description={data.categoriaString || data.assunto || "Solicitação de suporte"}
        actions={
          <ButtonLink to="/chamados" variant="secondary"><ArrowLeft /> Voltar</ButtonLink>
        }
      />

      <section className="ticket-overview">
        <div>
          <span>Status atual</span>
          <StatusBadge status={data.status} />
        </div>
        <div>
          <span>Prioridade</span>
          <PriorityBadge priority={data.prioridade} />
        </div>
        <div>
          <span>Aberto em</span>
          <strong><CalendarDays /> {formatDate(data.data)}</strong>
        </div>
        <div>
          <span>Solicitante</span>
          <strong><UserRound /> {data.usuario}</strong>
        </div>
      </section>

      <div className="ticket-detail-grid">
        <div className="space-y-5">
          <Card className="detail-card">
            <div className="section-heading">
              <div><h2>Descrição do problema</h2><p>Informações enviadas na abertura.</p></div>
            </div>
            <p className="problem-copy">{plainText(data.problema) || "Nenhuma descrição informada."}</p>
            {data.anexosNovos?.length ? (
              <div className="opening-images">
                {data.anexosNovos.map((attachment, index) => (
                  <OpeningAttachment
                    key={attachment}
                    ticketId={id}
                    attachment={attachment}
                    index={index}
                    onDownload={() => void downloadAttachment(attachment, index)}
                  />
                ))}
              </div>
            ) : null}
          </Card>

          <Card className="detail-card">
            <div className="section-heading">
              <div><h2>Linha do tempo</h2><p>Acompanhe todas as interações deste chamado.</p></div>
              <span className="activity-count">{steps.length} atualizações</span>
            </div>
            <div className="timeline">
              {steps.length ? (
                steps.map((step, index) => (
                  <article key={`${step.data}-${index}`} className="timeline-item">
                    <span className="timeline-marker"><MessageSquareText /></span>
                    <div>
                      <header>
                        <div><strong>{step.usuario || "Equipe de suporte"}</strong><small>{formatDate(step.data)}</small></div>
                        <StatusBadge status={step.status} compact />
                      </header>
                      <p>{plainText(step.descricao) || "Status do chamado atualizado."}</p>
                      {step.anexosNovos?.length ? (
                        <div className="timeline-attachments">
                          {step.anexosNovos.map((attachment, attachmentIndex) => (
                            <a
                              key={attachment}
                              href="#"
                              onClick={(event) => {
                                event.preventDefault();
                                void downloadAttachment(attachment, attachmentIndex);
                              }}
                            >
                              {isImageAttachment(attachment) ? <Image /> : <FileText />}
                              <span>
                                <strong>{attachmentName(attachment, attachmentIndex)}</strong>
                                <small>Baixar arquivo</small>
                              </span>
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </article>
                ))
              ) : (
                <div className="empty-timeline">
                  <CheckCircle2 />
                  <p>Chamado registrado. A próxima atualização aparecerá aqui.</p>
                </div>
              )}
            </div>
          </Card>

          <Card className="detail-card reply-card">
            <div className="section-heading">
              <div><h2>Adicionar atualização</h2><p>Envie novas informações para a equipe responsável.</p></div>
            </div>
            <form onSubmit={submitStep}>
              <Select value={statusId} onChange={(event) => setStatusId(event.target.value)}>
                <option value="">Selecione o novo status</option>
                {availableStatuses.map((item) => (
                  <option key={item.id} value={item.id}>{item.status}</option>
                ))}
              </Select>
              <Textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onPaste={(event) => {
                  const pastedFiles = Array.from(event.clipboardData.files);
                  if (pastedFiles.length) {
                    event.preventDefault();
                    addFiles(pastedFiles);
                    toast.success("Captura adicionada aos anexos.");
                  }
                }}
                placeholder="Escreva uma nova informação ou cole uma captura de tela com Ctrl+V..."
              />
              <div
                className="reply-upload"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  addFiles(Array.from(event.dataTransfer.files));
                }}
              >
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  accept={ATTACHMENT_ACCEPT}
                  onChange={(event) => {
                    addFiles(Array.from(event.target.files ?? []));
                    event.target.value = "";
                  }}
                />
                <button type="button" onClick={() => fileInput.current?.click()}>
                  <span><Paperclip /></span>
                  <span>
                    <strong>Anexar arquivos ou prints</strong>
                    <small>
                      Arraste, selecione ou cole um arquivo · até {MAX_ATTACHMENTS}{" "}
                      (imagem, PDF, Word, Excel...)
                    </small>
                  </span>
                </button>
                {files.length ? (
                  <div className="reply-file-list">
                    {files.map((file) => (
                      <div key={`${file.name}-${file.lastModified}`}>
                        {isImageAttachment(file.name) ? <Image /> : <FileText />}
                        <span>
                          <strong>{file.name}</strong>
                          <small>{formatFileSize(file.size)}</small>
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setFiles((current) => current.filter((item) => item !== file))
                          }
                          aria-label={`Remover ${file.name}`}
                        >
                          <Trash2 />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="reply-actions">
                <span>{message.length}/2000</span>
                <Button type="submit" loading={addStep.isPending}>
                  <Send /> Enviar atualização
                </Button>
              </div>
            </form>
          </Card>
        </div>

        <aside className="ticket-aside">
          <Card>
            <h3>Informações</h3>
            <dl>
              <div><dt><Building2 /> Empresa</dt><dd>{data.empresa}</dd></div>
              <div><dt><Phone /> Telefone</dt><dd>{data.telefone || "Não informado"}</dd></div>
              <div><dt><Mail /> E-mail</dt><dd>{data.usuarioEmail || "Não informado"}</dd></div>
              {data.unidade && <div><dt>Unidade</dt><dd>{data.unidade}</dd></div>}
              <div>
                <dt><Wrench /> Tipo</dt>
                <dd>
                  {isMira ? (
                    <Select
                      value={data.tipo ?? ""}
                      disabled={updateType.isPending}
                      onChange={(event) => updateType.mutate(Number(event.target.value))}
                    >
                      <option value="1">Suporte</option>
                      <option value="2">Desenvolvimento</option>
                    </Select>
                  ) : (
                    data.tipo === "2" ? "Desenvolvimento" : "Suporte"
                  )}
                </dd>
              </div>
            </dl>
          </Card>
          <div className="aside-help">
            <MessageSquareText />
            <h3>Mantenha o contexto</h3>
            <p>Use a linha do tempo para enviar novidades sem abrir outro chamado.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
